/**
 * Trials already reported from this browser.
 *
 * The success page polls until the user comes back premium, and it is a normal
 * URL: refreshing it, or returning to it later, ran the whole sequence again and
 * reported a second trial for the same subscription. The authoritative count now
 * comes from the Stripe webhook, and this keeps the browser event from
 * disagreeing with it more than it has to.
 */
const REPORTED_KEY = 'yumnia.trial_started.reported'

function alreadyReported (id) {
  if (!id) return false
  try {
    const seen = JSON.parse(localStorage.getItem(REPORTED_KEY) ?? '[]')
    return Array.isArray(seen) && seen.includes(id)
  } catch {
    return false
  }
}

function rememberReported (id) {
  if (!id) return
  try {
    const seen = JSON.parse(localStorage.getItem(REPORTED_KEY) ?? '[]')
    const next = Array.isArray(seen) ? seen : []
    if (!next.includes(id)) {
      next.push(id)
    }
    // A person has a handful of subscriptions at most; the cap is only there so
    // a corrupted value cannot grow without bound.
    localStorage.setItem(REPORTED_KEY, JSON.stringify(next.slice(-10)))
  } catch {
    // Private mode, blocked storage. Reporting twice is better than not at all.
  }
}

/**
 * @param {import('@/stores/consent').useConsentStore} consentStore
 * @param {Record<string, unknown> | null | undefined} user
 * @param {Record<string, unknown>} extra
 */
export async function trackTrialStartedIfApplicable (consentStore, user, extra = {}) {
  const { assume_trial: assumeTrial = false, ...eventExtra } = extra
  if (!user?.is_premium) {
    return
  }
  if (user.subscription_status !== 'trialing' && !assumeTrial) {
    return
  }

  // trackEvent intentionally becomes a no-op without analytics consent. Do not
  // mark the trial as reported in that case: doing so would suppress the real
  // event if consent is granted and this state is checked again later.
  if (!consentStore?.consent?.analytics) {
    return
  }

  // Keyed on the person. The subscription id is deliberately server-side only,
  // and the person is the right unit anyway: a repeat trial from someone who
  // already had one is not a new acquisition, and counting it would inflate the
  // conversion Google Ads bids on. The authoritative per-subscription record is
  // the trial_started milestone the Stripe webhook writes.
  const key = user.id ? `user-${user.id}` : null
  if (alreadyReported(key)) {
    return
  }

  await consentStore.trackEvent('trial_started', {
    trial_days: null,
    subscription_status: user.subscription_status ?? null,
    ...eventExtra,
  })

  rememberReported(key)
}
